/**
 * Utility functions for base64 conversion
 */

/**
 * Converts a File or Blob to base64 string
 * @param file - The file or blob to convert
 * @returns Promise<string> - The base64 encoded string
 */
export const fileToBase64 = (file: File | Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Converts a base64 string to a Blob
 * @param base64String - The base64 encoded string
 * @param mimeType - Optional MIME type (defaults to 'application/octet-stream')
 * @returns Blob - The converted blob
 */
export const base64ToBlob = (base64String: string, mimeType: string = 'application/octet-stream'): Blob => {
  // Remove data URL prefix if present (e.g., "data:image/jpeg;base64,")
  const base64Data = base64String.includes(',') 
    ? base64String.split(',')[1] 
    : base64String;
  
  // Convert base64 to binary
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  
  return new Blob([bytes], { type: mimeType });
};

/**
 * Converts a base64 string to a File object
 * @param base64String - The base64 encoded string
 * @param filename - The name for the file
 * @param mimeType - Optional MIME type (defaults to 'application/octet-stream')
 * @returns File - The converted file
 */
export const base64ToFile = (base64String: string, filename: string, mimeType: string = 'application/octet-stream'): File => {
  const blob = base64ToBlob(base64String, mimeType);
  return new File([blob], filename, { type: mimeType });
};

/**
 * Converts a base64 string to a downloadable URL
 * @param base64String - The base64 encoded string
 * @param mimeType - Optional MIME type (defaults to 'application/octet-stream')
 * @returns string - The object URL for downloading
 */
export const base64ToUrl = (base64String: string, mimeType: string = 'application/octet-stream'): string => {
  const blob = base64ToBlob(base64String, mimeType);
  return URL.createObjectURL(blob);
};

/**
 * Utility function to detect MIME type from base64 string
 * @param base64String - The base64 encoded string
 * @returns string - The detected MIME type
 */
export const detectMimeType = (base64String: string): string => {
  if (base64String.startsWith('data:')) {
    const mimeMatch = base64String.match(/data:([^;]+);/);
    return mimeMatch ? mimeMatch[1] : 'application/octet-stream';
  }
  
  // Try to detect from base64 content
  const header = base64String.substring(0, 20);
  
  if (header.startsWith('/9j/') || header.startsWith('iVBORw0KGgo')) {
    return 'image/jpeg';
  }
  
  if (header.startsWith('iVBORw0KGgo')) {
    return 'image/png';
  }
  
  if (header.startsWith('UklGR')) {
    return 'image/webp';
  }
  
  if (header.startsWith('R0lGOD')) {
    return 'image/gif';
  }
  
  return 'application/octet-stream';
};

/**
 * Enhanced file to base64 conversion with automatic MIME type detection
 * @param file - The file to convert
 * @param includeMimeType - Whether to include MIME type in the result (default: true)
 * @returns Promise<string> - The base64 encoded string with or without MIME type prefix
 */
export const fileToBase64WithMime = async (file: File | Blob, includeMimeType: boolean = true): Promise<string> => {
  const base64 = await fileToBase64(file);
  
  if (includeMimeType && file instanceof File) {
    return `data:${file.type};base64,${base64.split(',')[1]}`;
  }
  
  return base64;
};
